import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Typography } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { BORDER_RADIUS } from "app/theme";
import DetailDialog from "common/components/DetailDialog";
import { getFormattedCategoryName } from "features/programmesActions/ActionList";
import { useActionEvidence } from "model";
import { useEffect, useRef, useState } from "react";
import {
  AnimalPerformanceCard,
  BiodiversityCard,
  CarbonFootprintCard,
  MilkQualityCard,
  WaterSoilCard,
} from "../AssessmentCards";
import type { FarmerActionData } from "../hooks/useInsightsDashboardQueries";
import {
  useFarmerActions,
  useProgrammeInfo,
} from "../hooks/useInsightsDashboardQueries";
import type { InsightCategory } from "../InsightDetailDialog";
import InsightDetailDialog from "../InsightDetailDialog";
import ActionDetailView from "./ActionDetailView";
import FarmerFilesTable from "./FarmerFilesTable";

// ─── Actions Table ──────────────────────────────────────────────

function ActionsTable({
  farmerGuid,
  onActionSelect,
  evidenceCountOverrides,
}: {
  farmerGuid: string;
  onActionSelect: (guid: string) => void;
  evidenceCountOverrides: Record<string, number>;
}) {
  const { data, isLoading } = useFarmerActions(farmerGuid);
  const actions = data?.data ?? [];

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: BORDER_RADIUS.small + "px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        mb: 3,
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b" }}
        >
          Submitted Actions
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr auto 40px",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          Category
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          Action
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
        >
          Submitted
        </Typography>
        <Box />
      </Box>

      {isLoading ? (
        [1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr auto 40px",
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #f4f4f5",
            }}
          >
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width={160} height={18} />
            <Skeleton
              variant="text"
              width={70}
              height={18}
              sx={{ ml: "auto" }}
            />
            <Box />
          </Box>
        ))
      ) : actions.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No actions submitted yet
          </Typography>
        </Box>
      ) : (
        actions.map((action) => {
          const guid = action["SubmittedActions.guid"];
          const evidenceCount =
            evidenceCountOverrides[guid] ??
            (Number(action["SubmittedActions.evidenceCount"]) || 0);

          return (
            <Box
              key={guid}
              sx={{
                borderBottom: "1px solid #f4f4f5",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 2fr auto 40px",
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#fafafa" },
                }}
                onClick={() => onActionSelect(guid)}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#71717a", fontSize: "0.8rem" }}
                >
                  {getFormattedCategoryName(
                    parseInt(action["SubmittedActions.categoryId"] ?? "0", 10)
                  ) || action["SubmittedActions.category"]}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#18181b", fontWeight: 500 }}
                >
                  {action["SubmittedActions.action"]}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#71717a",
                    textAlign: "right",
                    fontSize: "0.8rem",
                  }}
                >
                  {action["SubmittedActions.submissionDate"]
                    ? new Date(
                        action["SubmittedActions.submissionDate"]
                      ).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "-"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 0.25,
                  }}
                >
                  {evidenceCount > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "#16a34a",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 14 }} />
                      {evidenceCount}
                    </Box>
                  )}
                  <IconButton size="small" sx={{ p: 0 }}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
}

// ─── Main farmer detail dialog ──────────────────────────────────

export default function FarmerDetailDialog({
  open,
  farmerGuid,
  farmerName,
  onClose,
}: {
  open: boolean;
  farmerGuid: string | null;
  farmerName: string | null;
  onClose: () => void;
}) {
  const [detailCategory, setDetailCategory] = useState<InsightCategory | null>(
    null
  );
  const [selectedActionGuid, setSelectedActionGuid] = useState<string | null>(
    null
  );
  const [evidenceCountOverrides, setEvidenceCountOverrides] = useState<
    Record<string, number>
  >({});
  const { data: programmeInfo } = useProgrammeInfo();
  const { data: farmerActionsData } = useFarmerActions(farmerGuid ?? undefined);
  const programmeType =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeType"] ?? "BEEF";
  const isDairy = programmeType === "DAIRY";

  const selectedAction: FarmerActionData | undefined = selectedActionGuid
    ? farmerActionsData?.data?.find(
        (a) => a["SubmittedActions.guid"] === selectedActionGuid
      )
    : undefined;

  // Track evidence count from the live evidence list for the currently open action
  const { data: currentEvidence } = useActionEvidence(
    selectedActionGuid ?? undefined
  );
  const lastEvidenceRef = useRef<{ guid: string; count: number } | null>(null);

  useEffect(() => {
    if (selectedActionGuid && currentEvidence) {
      lastEvidenceRef.current = {
        guid: selectedActionGuid,
        count: currentEvidence.length,
      };
    }
  }, [selectedActionGuid, currentEvidence]);

  const handleBackFromAction = () => {
    const last = lastEvidenceRef.current;
    if (last) {
      setEvidenceCountOverrides((prev) => ({
        ...prev,
        [last.guid]: last.count,
      }));
    }
    setSelectedActionGuid(null);
  };

  // Reset internal navigation state when dialog closes
  useEffect(() => {
    if (!open) {
      setDetailCategory(null);
      setSelectedActionGuid(null);
    }
  }, [open]);

  if (!farmerGuid) return null;

  return (
    <>
      <DetailDialog
        open={open}
        onClose={onClose}
        title={farmerName ?? "Farmer Details"}
        contentSx={{ backgroundColor: "#F7FCF1" }}
      >
        <Box
          sx={{
            maxWidth: 800,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 3,
          }}
        >
          <CarbonFootprintCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("carbon-footprint")}
          />
          <BiodiversityCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("biodiversity")}
          />
          <WaterSoilCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("water-soil")}
          />
          {isDairy ? (
            <MilkQualityCard
              farmerGuid={farmerGuid}
              onClick={() => setDetailCategory("milk-quality")}
            />
          ) : (
            <AnimalPerformanceCard
              farmerGuid={farmerGuid}
              onClick={() => setDetailCategory("animal-performance")}
            />
          )}
        </Box>

        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          <ActionsTable
            farmerGuid={farmerGuid}
            onActionSelect={setSelectedActionGuid}
            evidenceCountOverrides={evidenceCountOverrides}
          />
          <FarmerFilesTable farmerGuid={farmerGuid} />
        </Box>
      </DetailDialog>

      <InsightDetailDialog
        open={detailCategory !== null}
        category={detailCategory}
        onClose={onClose}
        onBack={() => setDetailCategory(null)}
        farmerGuid={farmerGuid}
      />

      <DetailDialog
        open={selectedAction !== undefined}
        onClose={onClose}
        onBack={handleBackFromAction}
        title={selectedAction?.["SubmittedActions.action"] ?? "Action Details"}
      >
        {selectedAction && (
          <ActionDetailView
            action={selectedAction}
            farmerGuid={farmerGuid ?? undefined}
          />
        )}
      </DetailDialog>
    </>
  );
}
